import { createSelector } from 'reselect';
import pathOr from 'ramda/src/pathOr';
import isEmpty from 'ramda/src/isEmpty';
import i18n from 'invision-core/src/components/i18n/i18n';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { BusinessUnitFetchFeatureToggleValuesSelector } from 'invision-core/src/components/configurable/configurable.selectors';
import { formatCountryAndPostalCode } from 'invision-core/src/utilities/address.helper';
import { getUpperLimitAccess, hasAccess } from 'invision-core/src/components/security/permission.service';
import { parseFilenameFromPath } from 'invision-core/src/components/upload/upload.helper';
import { UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
import CustomerLocaleKeys from '../../locales/keys';
import { IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_ERROR_STATUSES, IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_PENDING_STATUSES, IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES, IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES_WITH_RESULTS_SUMMARY, IMPORT_BULK_SERVICE_ATTRIBUTES_UPLOAD_STATUSES, MAX_NUM_IMPORT_UPLOADABLE_FILES, ORDER_TYPES } from '../../components/customer/orderHistory/orderDetails/order.details.constants';
import { ConvergentBillerOrderDetailsSelector, ConvergentBillerOrderPaymentInstruments } from './customer.convergent.biller.selectors';
import { CurrentCustomerIdSelector } from './customer.selectors';
import { generateFormattedFileResult } from '../../components/customer/orderHistory/orderDetails/importBulkAttributesModal/import.bulk.attributes.modal.helper';
import { ISSUE_CREDIT_STANDARD_CURRENCY_LIMIT, OVERRIDE_BILLING_EFFECTIVE_DATE } from '../../security.attributes';
import { ORDER_STATUSES } from '../../../src/components/shared/constants/order.status.constants';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../constants/common.constants';
import { BILLING_EFFECTIVE_DATE_ORDER_OPTIONS, SCHEDULED_ORDER_OPTIONS } from '../../customercare.constants';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.orderDetails;
};
export var OrderDetailsImportServiceAttributesJobDetailsSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.importServiceAttributesJobDetails;
});
export var OrderDetailsImportServiceAttributesJobDetailsErrorSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.importServiceAttributesJobDetailsError;
});
export var OrderDetailsImportServiceAttributesJobPendingFilesSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.importServiceAttributesJobPendingFiles;
});
export var OrderDetailsImportServiceAttributesJobsPendingUpdatedStatusSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.importServiceAttributesJobsPendingUpdatedStatus;
});
var EMPTY_JOB_DETAILS_VIEW_MODEL = {
  importHistory: [],
  importHistorySummary: null,
  jobId: null,
  orderId: null,
  pendingFiles: [],
  pendingUpdatedStatus: false,
  pricingPlanId: null,
  pricingPlanName: null,
  statusId: null,
  subscriberId: null
};
export var OrderDetailsImportServiceAttributesJobDetailsViewModelSelector = createSelector([CurrentCustomerIdSelector, OrderDetailsImportServiceAttributesJobDetailsSelector, OrderDetailsImportServiceAttributesJobPendingFilesSelector, OrderDetailsImportServiceAttributesJobsPendingUpdatedStatusSelector], function (subscriberId, jobDetails, pendingFiles, pendingJobs) {
  var jobDetailsViewModel;
  if (jobDetails) {
    var mutableJobDetails = jobDetails.asMutable({
      deep: true
    });
    var mutablePendingFiles = pendingFiles.asMutable({
      deep: true
    });
    var numJobExtracts = mutableJobDetails.BulkFileExtractDetail ? Math.max(0, mutableJobDetails.BulkFileExtractDetail.length) : 0;
    var lastJobExtractIndex = Math.max(0, numJobExtracts - 1);
    jobDetailsViewModel = Object.assign({}, EMPTY_JOB_DETAILS_VIEW_MODEL, {
      importHistory: [],
      jobId: mutableJobDetails.BulkExtractJobId,
      orderId: mutableJobDetails.OrderId,
      pendingFiles: mutablePendingFiles || [],
      pendingUpdatedStatus: !!pendingJobs[mutableJobDetails.PricingPlanId],
      pricingPlanId: mutableJobDetails.PricingPlanId,
      pricingPlanName: mutableJobDetails.PricingPlanName,
      subscriberId: subscriberId
    });
    if (0 === numJobExtracts) {
      jobDetailsViewModel.statusId = IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES.NOT_STARTED;
    } else {
      mutableJobDetails.BulkFileExtractDetail.forEach(function (extract, index) {
        var errorDetailSummary = null;
        var importResults = [];
        if (extract.BulkExtractErrors) {
          importResults = extract.BulkExtractErrors.map(function (error) {
            return generateFormattedFileResult(IMPORT_BULK_SERVICE_ATTRIBUTES_UPLOAD_STATUSES.FAILURE, error.ErrorDescription);
          });
        } else if (IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES.FINISHED === extract.Status) {
          var formattedResult = generateFormattedFileResult(IMPORT_BULK_SERVICE_ATTRIBUTES_UPLOAD_STATUSES.SUCCESS, i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.IMPORT_BULK_SERVICE_ATTRIBUTES.FILE_IMPORTED_SUCCESSFULLY));
          importResults = [formattedResult];
        } else if (-1 !== IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_PENDING_STATUSES.indexOf(extract.Status)) {
          var _formattedResult = generateFormattedFileResult(IMPORT_BULK_SERVICE_ATTRIBUTES_UPLOAD_STATUSES.PROGRESS, i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.IMPORT_BULK_SERVICE_ATTRIBUTES.FILE_PROCESSING));
          importResults = [_formattedResult];
        }
        if (-1 !== IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_ERROR_STATUSES.indexOf(extract.Status) && extract.BulkExtractErrorDetailFileSummary) {
          errorDetailSummary = {
            contentType: extract.BulkExtractErrorDetailFileSummary.ContentType,
            fileLocation: extract.BulkExtractErrorDetailFileSummary.ErrorDetailFileLocation,
            isErrorFileAvailable: extract.BulkExtractErrorDetailFileSummary.IsAvailable
          };
        }
        if (lastJobExtractIndex === index) {
          jobDetailsViewModel.statusId = extract.Status;
          if (-1 !== IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES_WITH_RESULTS_SUMMARY.indexOf(extract.Status)) {
            jobDetailsViewModel.importHistorySummary = {
              dateAdded: extract.Added,
              dateLastModified: extract.Modified,
              numFailedItems: extract.FailureRecords,
              numSuccessfulItems: extract.SuccessRecords,
              numTotalItems: extract.TotalRecords
            };
          }
        }
        var file = {
          dateAdded: extract.Added,
          dateLastModified: extract.Modified,
          errorDetailSummary: errorDetailSummary,
          extractId: extract.BulkFileExtractId,
          importResults: importResults,
          name: parseFilenameFromPath(extract.FileLocation),
          statusId: extract.Status
        };

        // NOTE: Job extracts are returned in ascending order by date added,
        // but are displayed in descending order
        jobDetailsViewModel.importHistory.unshift(file);
      });
    }
  }
  return jobDetailsViewModel || EMPTY_JOB_DETAILS_VIEW_MODEL;
});
export var OrderDetailsImportServiceAttributesUploadConfigurationSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.importServiceAttributesUploadConfiguration;
});
export var OrderDetailsBulkServiceAttributesJobIsImportingSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.isImportingServiceAttributes;
});
export var OrderDetailsIsRetrievingImportServiceAttributesJobDetailsSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.isRetrievingImportServiceAttributesJobDetails;
});
export var OrderDetailsIsRetrievingImportServiceAttributesUploadConfigurationSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.isRetrievingImportServiceAttributesUploadConfiguration;
});
export var OrderDetailsIsSendingBulkServiceAttributesUploadNotificationSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.isSendingBulkServiceAttributesUploadNotification;
});
export var OrderDetailsIsExportingBulkServiceAttributesSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.isExportingBulkServiceAttributes;
});
export var OrderDetailsExportingBulkServiceAttributesErrorSelector = createSelector([recoverableUIStateSelector], function (recoverableUIStateSelector) {
  return recoverableUIStateSelector.exportingBulkServicesAttributeError;
});
export var OrderDetailsBulkServiceAttributesJobIsProcessingSelector = createSelector([OrderDetailsImportServiceAttributesJobDetailsViewModelSelector], function (jobDetailsViewModel) {
  return -1 !== IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_PENDING_STATUSES.indexOf(jobDetailsViewModel.statusId);
});
export var OrderDetailsImportServiceAttributesFormSubmissionIsDisabledSelector = createSelector([OrderDetailsImportServiceAttributesJobDetailsViewModelSelector, OrderDetailsBulkServiceAttributesJobIsImportingSelector, OrderDetailsIsRetrievingImportServiceAttributesJobDetailsSelector, OrderDetailsIsRetrievingImportServiceAttributesUploadConfigurationSelector, OrderDetailsIsSendingBulkServiceAttributesUploadNotificationSelector, OrderDetailsBulkServiceAttributesJobIsProcessingSelector], function (jobDetailsViewModel, isImportingServiceAttributes, isRetrievingJobDetails, isRetrievingUploadConfig, isSendingUploadNotification, isProcessingServiceAttributes) {
  var actionIsInProgress = isRetrievingUploadConfig || isRetrievingJobDetails || isImportingServiceAttributes || isSendingUploadNotification || isProcessingServiceAttributes;
  return actionIsInProgress || !jobDetailsViewModel.jobId || jobDetailsViewModel.pendingUpdatedStatus || jobDetailsViewModel.statusId === IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES.FINISHED || jobDetailsViewModel.statusId === IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES.FINISHED_WITH_ERRORS;
});
export var OrderDetailsImportServiceAttributesFormEditingIsDisabledSelector = createSelector([OrderDetailsImportServiceAttributesFormSubmissionIsDisabledSelector, OrderDetailsImportServiceAttributesJobDetailsViewModelSelector], function (formSubmissionIsDisabled, jobDetailsViewModel) {
  return formSubmissionIsDisabled || jobDetailsViewModel.pendingFiles.length >= MAX_NUM_IMPORT_UPLOADABLE_FILES || jobDetailsViewModel.statusId === IMPORT_BULK_SERVICE_ATTRIBUTES_JOB_STATUSES.FINISHED_WITH_ERRORS;
});
export var OrderDetailTabsSelector = createSelector([recoverableUIStateSelector, ConvergentBillerOrderDetailsSelector], function (orderDetailUiState, currentOrderDetails) {
  var tabs = [{
    name: i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.ORDER_DETAILS)
  }, {
    name: i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.WORKFLOW)
  }, {
    name: i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.ORDER_PAYMENTS)
  }];
  if (currentOrderDetails && currentOrderDetails.Type === ORDER_TYPES.GIFT) {
    tabs.push({
      name: i18n.translate(CustomerLocaleKeys.ORDER_DETAILS.NOTIFICATION_HISTORY)
    });
  }
  return tabs.map(function (tab, index) {
    tab.active = index + 1 === orderDetailUiState.selectedTab;
    return tab;
  });
});
export var OrderDetailsDataSelector = createImmutableSelector([recoverableUIStateSelector], function (orderDetails) {
  return orderDetails.data;
});
export var CurrentOrderBillingEffectiveDateSelector = createImmutableSelector([ConvergentBillerOrderDetailsSelector], function (currentOrderDetails) {
  return currentOrderDetails && currentOrderDetails.Items && (currentOrderDetails.Items.find(function (item) {
    return item.BillingEffectiveDate;
  }) || EMPTY_OBJECT).BillingEffectiveDate || null;
});
export var SelectedOfferExecutionOptionsSelector = createImmutableSelector([OrderDetailsDataSelector, CurrentOrderBillingEffectiveDateSelector, ConvergentBillerOrderDetailsSelector, UserSecurityAttributesSelector], function (orderDetailsData, billingEffectiveDate, currentOrderDetails, userSecurityAttributes) {
  return isEmpty(orderDetailsData.executionOptions) ? {
    billingEffectiveDate: billingEffectiveDate,
    billingEffectiveIntention: billingEffectiveDate ? "".concat(BILLING_EFFECTIVE_DATE_ORDER_OPTIONS.ON_SPECIFIED_DATE) : hasAccess(userSecurityAttributes, OVERRIDE_BILLING_EFFECTIVE_DATE) ? "".concat(BILLING_EFFECTIVE_DATE_ORDER_OPTIONS.ON_ORDER_COMPLETION) : undefined,
    effective: "".concat(SCHEDULED_ORDER_OPTIONS.ON_SPECIFIED_DATE),
    specifiedDate: pathOr(null, ['FutureDatedOrder', 'ExecutionDate'], currentOrderDetails)
  } : orderDetailsData.executionOptions;
});
export var IsRetrievingOrderContextSelector = createImmutableSelector([recoverableUIStateSelector], function (orderDetails) {
  return orderDetails.isRetrievingOrderContext;
});
export var BillingEffectiveDateConfigurationSelector = createImmutableSelector([OrderDetailsDataSelector], function (data) {
  return data.billingEffectiveDateConfiguration;
});
export var BillingEffectiveDateSettingsSelector = createImmutableSelector([BillingEffectiveDateConfigurationSelector], function (configuration) {
  return {
    minimumDate: configuration.MinBillingEffectiveDate,
    pastDateMaxLength: configuration.PastBillingEffectiveDateMaxLength
  };
});
export var BillingEffectiveDateOptionsSelector = createImmutableSelector([BillingEffectiveDateConfigurationSelector], function (configuration) {
  return configuration.OrderExecutionToBillingEffectiveDateIntentions || EMPTY_ARRAY;
});
export var IsGiftRedemptionOrderSelector = createSelector([ConvergentBillerOrderDetailsSelector], function (currentOrderDetails) {
  return ORDER_TYPES.GIFT_REDEMPTION === pathOr(null, ['Type'], currentOrderDetails);
});
export var IsDbssOrderSelector = createSelector([ConvergentBillerOrderDetailsSelector], function (currentOrderDetails) {
  return !!pathOr(false, ['Items', 0, 'PricingPlan', 'HasConvergentBillerExtension'], currentOrderDetails);
});
export var OrderCanBeCreditedSelector = createSelector([ConvergentBillerOrderDetailsSelector, ConvergentBillerOrderPaymentInstruments], function (currentOrderDetails, currentOrderPaymentInstruments) {
  var totalAmount = pathOr(0, ['Totals', 'NetTotalAmount'], currentOrderDetails);
  return !!(currentOrderPaymentInstruments.length && totalAmount > 0);
});
export var CurrentOrderHasShippingAddressSelector = createSelector([ConvergentBillerOrderDetailsSelector], function (currentOrderDetails) {
  return !!pathOr(false, ['ShippingAddress'], currentOrderDetails);
});
export var FormattedShippingCityStateSelector = createSelector([ConvergentBillerOrderDetailsSelector, CurrentOrderHasShippingAddressSelector], function (currentOrderDetails, currentOrderHasShippingAddress) {
  if (!currentOrderHasShippingAddress) {
    return '';
  }
  var _ref = currentOrderDetails.ShippingAddress || {},
    _ref$City = _ref.City,
    City = _ref$City === void 0 ? '' : _ref$City,
    _ref$State = _ref.State,
    State = _ref$State === void 0 ? '' : _ref$State,
    _ref$Country = _ref.Country,
    Country = _ref$Country === void 0 ? '' : _ref$Country,
    _ref$PostalCode = _ref.PostalCode,
    PostalCode = _ref$PostalCode === void 0 ? '' : _ref$PostalCode;
  var countryAndPostalCode = formatCountryAndPostalCode(Country, PostalCode).countryAndPostalCode;
  return "".concat(City ? "".concat(City, ", ") : '').concat(State ? "".concat(State, ", ") : '').concat(countryAndPostalCode);
});
export var CreditOrderLimitSelector = createSelector([UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return getUpperLimitAccess(userSecurityAttributes, ISSUE_CREDIT_STANDARD_CURRENCY_LIMIT.id);
});
export var CanIssueOrderCreditSelector = createSelector([IsDbssOrderSelector, OrderCanBeCreditedSelector, CreditOrderLimitSelector], function (isDbssOrder, orderCanBeCredited, creditOrderLimit) {
  return !isDbssOrder && orderCanBeCredited && creditOrderLimit > 0;
});
export var ShowOrderDetailsExecutionDate = createSelector([ConvergentBillerOrderDetailsSelector], function (orderDetails) {
  return ORDER_STATUSES.PENDING === pathOr(null, ['FutureDatedOrder', 'Status'], orderDetails);
});
export var EnableOrderDetailsMinimumCommitmentFeatureToggleSelector = createSelector([BusinessUnitFetchFeatureToggleValuesSelector], function (featureToggle) {
  return featureToggle.InvTotalMinimumCommitmentEnabled;
});